import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { EventRecord } from "@src/store/pricing/queryEventsSlice";
import { IGlobalInventoryRawItem } from "@src/store/pricing/queryGlobalInventorySlice";
import { Theme } from "@src/store/settingsSlice";
import { StylesConfig } from "react-select";
import { stringUpperCase } from "./stringUtils";

export interface OptionType<T = string | number> {
	value: T;
	label: string;
	fieldLabel?: string;
}

export const tagConfig: StylesConfig<OptionType> = {
	control: (provided, state) => ({
		...provided,
		minHeight: "20px",
		fontSize: "1.4rem",
		borderColor: state.isFocused ? "none" : "lightgray",
		boxShadow: "none",
		"&:hover": {
			borderColor: "gray",
		},
	}),
	input: (provided) => ({
		...provided,
		padding: "0px",
		margin: "1px",
	}),
	container: (provided) => ({
		...provided,
		flexGrow: 1,
	}),
	indicatorsContainer: (provided) => ({
		...provided,
		padding: "0px 2px",
	}),
	clearIndicator: (provided) => ({
		...provided,
		padding: "0px 2px",
	}),
	menu: (provided) => ({
		...provided,
		background: "white",
		zIndex: 9,
	}),
	option: (provided, state) => ({
		...provided,
		fontSize: "1.4rem",
		backgroundColor: "white",
		color: state.isSelected ? "var(--frontBlue)" : "black",
		"&:hover": {
			backgroundColor: "#d1d1db",
		},
	}),
	noOptionsMessage: (provided) => ({
		...provided,
		fontSize: "1.4rem",
	}),
	loadingMessage: (provided) => ({
		...provided,
		fontSize: "1.4rem",
	}),
};

export const filtersConfig = (
	_data: string,
	theme: Theme,
	bg?: { light: string; dark: string },
	menuMaxHeight = 200,
): StylesConfig<OptionType> => {
	const isLightTheme = theme === Theme.Light;
	const itemBackground = bg ? (isLightTheme ? bg.light : bg.dark) : isLightTheme ? "#fafafa" : "#1c1c1c";
	const itemBackgroundSelected = isLightTheme ? "#EDEEF4" : "#2f2f2f";
	const itemBorder = isLightTheme ? "#ddddda" : "#2f2f2f";
	const itemTextColor = isLightTheme ? "#212121" : "#fafafa";
	const itemPlaceholderColor = isLightTheme ? "#888888" : "#a3a3a3";
	const itemHoverColor = isLightTheme ? "#888888" : "#a3a3a3";

	return {
		control: (provided) => ({
			...provided,
			minHeight: "32px",
			height: "100%",
			fontSize: "1.6rem",
			border: `1px solid ${itemBorder}`,
			background: itemBackground,
			flexWrap: "nowrap",
			boxShadow: "none",
			"&:hover": {
				borderColor: itemHoverColor,
			},
		}),
		input: (provided) => ({
			...provided,
			color: itemTextColor,
			padding: "0px",
			margin: "1px",
			opacity: 1,
		}),
		container: (provided) => ({
			...provided,
			flexGrow: 1,
		}),
		singleValue: (provided) => ({
			...provided,
			color: itemTextColor,
		}),
		indicatorsContainer: (provided) => ({
			...provided,
			padding: "0px 2px",
		}),
		clearIndicator: (provided) => ({
			...provided,
			padding: "0px 2px",
		}),
		menu: (provided) => ({
			...provided,
			background: itemBackground,
			border: `1px solid ${itemBorder}`,
			zIndex: 9,
		}),
		menuList: (provided) => ({
			...provided,
			maxHeight: `${menuMaxHeight}px`,
		}),
		valueContainer: (provided) => ({
			...provided,
			paddingRight: "0px",
		}),
		option: (provided, state) => ({
			...provided,
			fontSize: "1.4rem",
			backgroundColor: state.isSelected ? itemBackgroundSelected : itemBackground,
			color: itemTextColor,
			"&:hover": {
				backgroundColor: itemBackgroundSelected,
			},
		}),
		noOptionsMessage: (provided) => ({
			...provided,
			fontSize: "1.4rem",
		}),
		loadingMessage: (provided) => ({
			...provided,
			fontSize: "1.4rem",
		}),
		dropdownIndicator: (provided) => ({
			...provided,
			padding: "4px",
		}),
		placeholder: (provided) => ({
			...provided,
			fontSize: "1.4rem",
			color: itemPlaceholderColor,
			whiteSpace: "nowrap",
		}),
	};
};

type OptionTypeMulti = {
	label: string;
	value: string;
};

export const filtersConfigMulty = (_data: string, theme: Theme, controlSize: "m" | "sm"): StylesConfig<OptionTypeMulti> => {
	const isLightTheme = theme === Theme.Light;
	const itemBackground = isLightTheme ? "#fafafa" : "#0d0d0d";
	const itemBackgroundSelected = isLightTheme ? "#EDEEF4" : "#2f2f2f";
	const itemBorder = isLightTheme ? "#ddddda" : "#2f2f2f";
	const itemTextColor = isLightTheme ? "#212121" : "#fafafa";
	const itemPlaceholderColor = isLightTheme ? "#888888" : "#a3a3a3";
	const itemHoverColor = isLightTheme ? "#888888" : "#a3a3a3";

	return {
		control: (provided) => ({
			...provided,
			minHeight: "20px",
			height: controlSize === "sm" ? "25px" : undefined,
			fontSize: "1.4rem",
			border: `1px solid ${itemBorder}`,
			background: itemBackground,
			flexWrap: "wrap", // Change to "wrap" for multi-select support
			boxShadow: "none",
			"&:hover": {
				borderColor: itemHoverColor,
			},
		}),
		input: (provided) => ({
			...provided,
			padding: "0px",
			margin: "1px",
			color: itemTextColor,
		}),
		container: (provided) => ({
			...provided,
			flexGrow: 1,
		}),
		singleValue: (provided) => ({
			...provided,
			color: itemTextColor,
		}),
		indicatorsContainer: (provided) => ({
			...provided,
			padding: "0px 2px",
			height: controlSize === "sm" ? "25px" : undefined,
		}),
		clearIndicator: (provided) => ({
			...provided,
			padding: "0px 2px",
		}),
		menu: (provided) => ({
			...provided,
			background: itemBackground,
			border: `1px solid ${itemBorder}`,
			zIndex: 9,
		}),
		menuList: (provided) => ({
			...provided,
			maxHeight: "200px",
		}),
		valueContainer: (provided) => ({
			...provided,
			paddingRight: "0px",
			flexWrap: "wrap", // Necessary for multi-value wrapping
		}),
		option: (provided, state) => ({
			...provided,
			fontSize: "1.4rem",
			backgroundColor: state.isSelected ? itemBackgroundSelected : itemBackground,
			color: itemTextColor,
			"&:hover": {
				backgroundColor: itemBackgroundSelected,
			},
		}),
		noOptionsMessage: (provided) => ({
			...provided,
			fontSize: "1.4rem",
		}),
		loadingMessage: (provided) => ({
			...provided,
			fontSize: "1.4rem",
		}),
		dropdownIndicator: (provided) => ({
			...provided,
			padding: "4px",
		}),
		placeholder: (provided) => ({
			...provided,
			fontSize: "1.4rem",
			color: itemPlaceholderColor,
			whiteSpace: "nowrap",
		}),
		multiValue: (provided) => ({
			...provided,
			backgroundColor: itemBackgroundSelected,
			border: `1px solid ${itemBorder}`,
			borderRadius: "2px",
			padding: "0 2px",
			margin: "2px",
		}),
		multiValueLabel: (provided) => ({
			...provided,
			color: itemTextColor,
			fontSize: "1.4rem",
			padding: "2px",
		}),
		multiValueRemove: (provided) => ({
			...provided,
			color: itemTextColor,
			padding: "2px",
			"&:hover": {
				backgroundColor: itemHoverColor,
				color: isLightTheme ? "#fff" : "#000",
			},
		}),
	};
};

export const editCustomComponents = {
	DropdownIndicator: null,
};

export const getEventOptions = (events: Array<EventRecord>, name: keyof EventRecord) => {
	if (events.length == 0) return [];
	return Array.from(new Set(events.map((x) => String(x[name]).toLowerCase()))).map((x) => ({
		label: stringUpperCase(x),
		value: stringUpperCase(x),
	}));
};

export const getEventIdOptions = (
	events: Array<EventRecord>,
	labelKey: keyof EventRecord,
	valueKey: keyof EventRecord,
): Array<OptionType<number>> => {
	if (events.length === 0) return [];
	const byId = new Map<number, string>();
	events.forEach((event) => {
		const id = Number(event[valueKey]);
		const label = event[labelKey];
		if (!id || byId.has(id) || typeof label !== "string" || label.length === 0) return;
		byId.set(id, label);
	});
	return Array.from(byId.entries())
		.map(([value, label]) => ({ value, label }))
		.sort((a, b) => a.label.localeCompare(b.label));
};

export const getInventoryOptions = (
	inventory: Array<EventInventoryRecord>,
	name: "Listing_ID" | "Row" | "Days_Since_Last_Price_Update" | "PO_ID",
) => {
	return Array.from(new Set(inventory.map((x) => x[name]))).map((x) => ({
		label: String(x),
		value: String(x),
	}));
};

export const getGlobalInventoryOptions = (
	inventory: Array<IGlobalInventoryRawItem>,
	name: "Cat_Desc" | "Event_Headliner" | "Venue_Name",
) => {
	if (inventory.length == 0) return [];
	return Array.from(
		new Set(
			inventory
				.map((x) => x[name])
				.filter((v): v is string => typeof v === "string" && v.length > 0)
				.map((v) => v.toLowerCase()),
		),
	).map((x) => ({
		label: stringUpperCase(x),
		value: stringUpperCase(x),
	}));
};
